import { WarningOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import {
  createContext,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';

import type {
  ConfirmContext,
  ConfirmOptions,
  Nullable,
} from '@app/providers/ConfirmProvider/ConfirmProvider.types';
import { generateSpanWithDataTestId } from '@app/shared/utils';

const { confirm } = Modal;

export const ConfirmCtx = createContext<Nullable<ConfirmContext>>(null);

interface Props {
  children: ReactNode;
}

export function ConfirmProvider({ children }: Props) {
  const [confirmState, setConfirmState] =
    useState<Nullable<ConfirmOptions>>(null);

  const [open, toggle] = useState(false);

  const show = useCallback(
    (confirmOptions: Nullable<ConfirmOptions>) => {
      setConfirmState(confirmOptions);
      toggle(true);
    },
    [toggle]
  );

  const onConfirm = useCallback(() => {
    confirmState?.onConfirm?.();
    toggle(false);
  }, [confirmState, toggle]);

  const onCancel = useCallback(() => {
    confirmState?.onCancel?.();
    toggle(false);
  }, [confirmState, toggle]);

  const value = useMemo(() => ({ show }), [show]);

  useEffect(() => {
    if (open) {
      confirm({
        title: generateSpanWithDataTestId(
          confirmState?.title,
          'confirm-popup-title'
        ),
        icon: <WarningOutlined />,
        content: generateSpanWithDataTestId(
          confirmState?.subtitle,
          'confirm-popup-description'
        ),
        onOk: onConfirm,
        onCancel,
        cancelText: confirmState?.cancelText,
        okText: confirmState?.confirmText,
        cancelButtonProps: {
          'data-testid': 'cancel',
        },
        okButtonProps: {
          'data-testid': 'confirm-action',
        },
        modalRender: (node) => (
          <div data-testid="confirm-popup-unsaved-data">{node}</div>
        ),
      });
    }
  }, [open, confirmState, onConfirm, onCancel]);

  return <ConfirmCtx.Provider value={value}>{children}</ConfirmCtx.Provider>;
}
